import React from 'react';
import { useSelector } from 'react-redux';

import Loader from 'components/Loader';
import { getPaymentMethodLabel } from 'components/PaymentMethodSwitch';

import useDiscounts from 'hooks/useDiscounts';
import useHardOfferData from 'hooks/useHardOfferData';

import { formatMonetaryAmount } from 'utils/formatMonetaryAmount';
import { formatNumber } from 'utils/formatNumber';

import { getApplicationData } from 'selectors/getApplicationData';

import { goalIsPersonalFundsOnly, goalIsPayOffDebt } from 'handlers/yourGoal';

import styles from './LoanBreakdownCard.module.scss';

// We're summing the decimal parts of both fees to decide if rounding is needed.
// If their combined decimals exceed 0.5, we must account for it as an extra dollar.
// - If total decimals > 1.5: round both up (almost $2)
// - If between 0.5 and 1.5: round the larger one up and the smaller one down
// - If <= 0.5: no rounding needed
const roundBreakdownFees = (
  originationFee: number = 0,
  documentaryStampTaxFee: number = 0,
): { roundedOriginationFee: number; roundedDocumentaryFee: number } => {
  const originationFeeDecimal = originationFee % 1;
  const documentaryFeeDecimal = documentaryStampTaxFee % 1;
  const totalDecimalFees = originationFeeDecimal + documentaryFeeDecimal;

  let roundedOriginationFee = originationFee;
  let roundedDocumentaryFee = documentaryStampTaxFee;

  if (totalDecimalFees > 0.5) {
    if (totalDecimalFees > 1.5) {
      roundedOriginationFee = Math.ceil(originationFee);
      roundedDocumentaryFee = Math.ceil(documentaryStampTaxFee);
    } else if (originationFeeDecimal > documentaryFeeDecimal) {
      roundedOriginationFee = Math.ceil(originationFee);
      roundedDocumentaryFee = Math.floor(documentaryStampTaxFee);
    } else {
      roundedDocumentaryFee = Math.ceil(documentaryStampTaxFee);
      roundedOriginationFee = Math.floor(originationFee);
    }
  }

  return { roundedOriginationFee, roundedDocumentaryFee };
};

const LoanBreakdownCard = () => {
  const { applicableDiscounts, baseOriginationFee, baseInterestRate } = useDiscounts();
  const { application, isLoading: isApplicationLoading, apr, isAprLoading } = useSelector(getApplicationData);
  const { amountWeWillPay, amountWeWillSendYouToPay, additionalFunds } = useHardOfferData();

  const {
    documentaryStampTaxFee,
    originationFee,
    calculatedLoanAmount,
    loanAmount,
    borrowerStateOrProvince,
    loanGoal,
  } = application ?? {};

  const noInterestRateDiscount = !baseInterestRate || applicableDiscounts.length === 0;
  const payOffAmount = amountWeWillPay + amountWeWillSendYouToPay;
  const paymentPerPaycheck = formatMonetaryAmount(application?.amountPerPaycheck, true);
  const discountedOriginationFee = ((baseOriginationFee ?? 0) / 100) * (loanAmount ?? 0);
  const showOriginationFeeDiscount = parseFloat(discountedOriginationFee.toFixed(2)) > (originationFee ?? 0);
  const { roundedOriginationFee, roundedDocumentaryFee } = roundBreakdownFees(originationFee, documentaryStampTaxFee);

  return (
    <div className={styles.loanBreakdownCard}>
      <div className={styles.loanBreakdownHeader}>
        <div className={styles.paymentPerPaycheck}>
          {application && !isApplicationLoading ? <h2>{paymentPerPaycheck}</h2> : <Loader color="#9d86f9" size={25} />}
          <p>Per {getPaymentMethodLabel(application?.paymentMethod)}</p>
        </div>
        <div className={styles.paymentDetails}>
          <p>
            Months{' '}
            {application?.loanTermInMonths ? (
              <span>{application.loanTermInMonths}</span>
            ) : (
              <Loader color="#9d86f9" size={25} />
            )}
          </p>
          <p>
            Interest rate <span>{formatNumber(application?.interestRate, true)}%</span>
          </p>
        </div>
      </div>
      <div className={styles.loanBreakdownSeparator} />
      <div className={styles.loanBreakdown}>
        <div>
          <div className={styles.loanBreakdownItem}>
            <p>Total loan includes:</p>
            <span>{formatMonetaryAmount(calculatedLoanAmount)}</span>
          </div>
          <ul className={styles.loanBreakdownList}>
            {goalIsPayOffDebt(loanGoal) && (
              <li className={styles.loanBreakdownListItem}>
                <div className={styles.loanBreakdownItem}>
                  <p>Payoff amount</p>
                  <span>{formatMonetaryAmount(payOffAmount)}</span>
                </div>
              </li>
            )}
            {additionalFunds > 0 && (
              <li className={styles.loanBreakdownListItem}>
                <div className={styles.loanBreakdownItem}>
                  <p>{goalIsPersonalFundsOnly(loanGoal) ? 'Loan amount' : 'Extra funds'}</p>
                  <span>{formatMonetaryAmount(additionalFunds)}</span>
                </div>
              </li>
            )}
            <li className={styles.loanBreakdownListItem}>
              <div className={styles.loanBreakdownItem}>
                <p>Origination fee</p>
                <div className={styles.loanBreakdownComparison}>
                  {showOriginationFeeDiscount && (
                    <span className={styles.textStrikeThrough}>{formatMonetaryAmount(discountedOriginationFee)}</span>
                  )}
                  <span>{formatMonetaryAmount(roundedOriginationFee)}</span>
                </div>
              </div>
            </li>
            {documentaryStampTaxFee && documentaryStampTaxFee > 0 ? (
              <li className={styles.loanBreakdownListItem}>
                <div className={styles.loanBreakdownItem}>
                  <p>{borrowerStateOrProvince} Documentary fee</p>
                  <div className={styles.loanBreakdownComparison}>
                    <span>{formatMonetaryAmount(roundedDocumentaryFee)}</span>
                  </div>
                </div>
              </li>
            ) : null}
          </ul>
        </div>
        <div className={styles.loanBreakdownItem}>
          <p>
            {!noInterestRateDiscount ? 'Discounted ' : ''}Payment per{' '}
            {getPaymentMethodLabel(application?.paymentMethod)}
          </p>
          <div className={styles.loanBreakdownComparison}>
            {/* TODO: Update this discount with new backend data */}
            {/* {!noInterestRateDiscount && <span className={styles.textStrikeThrough}>{DISCOUNTED_PAYMENT_VALUE}</span>} */}
            {application && !isApplicationLoading ? (
              <span>{paymentPerPaycheck}</span>
            ) : (
              <Loader color="#9d86f9" size={25} />
            )}
          </div>
        </div>

        <div className={styles.loanBreakdownItem}>
          <p>{!noInterestRateDiscount ? 'Discounted ' : ''}Interest rate</p>
          <div className={styles.loanBreakdownComparison}>
            {isApplicationLoading ? (
              <Loader color="#9d86f9" size={25} />
            ) : (
              <>
                {baseInterestRate && <span className={styles.textStrikeThrough}>{baseInterestRate.toFixed(2)}%</span>}
                <span>{formatNumber(application?.interestRate, true)}%</span>
              </>
            )}
          </div>
        </div>

        <div className={styles.loanBreakdownItem}>
          <p>Annual Percentage Rate (APR)</p>
          <div className={styles.loanBreakdownComparison}>
            {isApplicationLoading || isAprLoading ? (
              <Loader color="#9d86f9" size={25} />
            ) : (
              <span>{formatNumber(apr, true)}%</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoanBreakdownCard;
